import {
  Box,
  Card,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FieldWrapper } from './Styles';
import {
  AvonMedicalCenterDto,
  AvonSpecializationDto,
} from '@act/shared/data-transfer-objects';
import { useState } from 'react';
import { Button } from '@act/storybook';
export type AvonFields = {
  medical_centers: string[];
  specialization: string;
  userType: 'provider' | 'support';
};

type AvonDetailsProps = {
  hasAvonUser: boolean;
  isAdmin: boolean;
  userRole?: string;
  avonUserDetails?: AvonFields;
  specializations: AvonSpecializationDto[];
  medicalCenters: AvonMedicalCenterDto[];
  handleAvonUserDetailsUpdated: (fields: AvonFields) => void;
};

const EditAvon = ({
  hasAvonUser,
  isAdmin,
  avonUserDetails,
  userRole,
  specializations,
  medicalCenters,
  handleAvonUserDetailsUpdated,
}: AvonDetailsProps) => {
  // Show message for existing users that Avon updates are temporarily disabled
  if (hasAvonUser) {
    return (
      <div style={{ flex: 1 }}>
        <Typography variant="h6">Avon Configuration</Typography>
        <Box
          sx={{
            p: 2,
            border: '1px solid #2196f3',
            borderRadius: 1,
            backgroundColor: '#e3f2fd',
            mt: 2,
          }}
        >
          <Typography variant="body2">
            ℹ️ Avon configuration updates are temporarily disabled while we
            improve the integration.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, display: 'block' }}>
            You can still update Avon settings directly from the Avon user
            interface.
          </Typography>
        </Box>
      </div>
    );
  }

  const filteredSpecializations =
    !!userRole && userRole !== 'PROVIDER'
      ? specializations.filter(item => item.type === 'support')
      : specializations;
  const specializationPicker = (
    <Select
      sx={{ minWidth: '200px', width: '100%' }}
      value={avonUserDetails?.specialization}
      onChange={e =>
        avonUserDetails &&
        handleAvonUserDetailsUpdated({
          ...avonUserDetails,
          specialization: e.target.value,
        })
      }
      disabled={hasAvonUser}
    >
      {filteredSpecializations &&
        filteredSpecializations.map((option, index) => (
          <MenuItem key={`${option}_${index}`} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
    </Select>
  );

  const [selectedMedCenterId, setSelectedMedCenterId] = useState<string>(null);
  const resetFields = () => {
    setSelectedMedCenterId(null);
  };

  const removeMedCenter = (idToRemove: string) => {
    if (avonUserDetails) {
      const updatedMedCenters = (avonUserDetails.medical_centers || []).filter(
        id => id !== idToRemove
      );
      handleAvonUserDetailsUpdated({
        ...avonUserDetails,
        medical_centers: updatedMedCenters,
      });
    }
  };

  const addMedCenter = () => {
    if (selectedMedCenterId && avonUserDetails) {
      const selectedMedCenters = avonUserDetails.medical_centers || [];

      selectedMedCenters.push(selectedMedCenterId);
      handleAvonUserDetailsUpdated({
        ...avonUserDetails,
        medical_centers: selectedMedCenters,
      });
      resetFields();
    }
  };
  const medCenterPicker = (
    <Select
      sx={{ minWidth: '200px', width: '100%' }}
      value={selectedMedCenterId}
      onChange={e => setSelectedMedCenterId(e.target.value)}
    >
      {medicalCenters &&
        medicalCenters.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.name} ({option.id})
          </MenuItem>
        ))}
    </Select>
  );
  const isAdminNoAvon = isAdmin && !hasAvonUser && !userRole;
  const isRegNoAvon = !isAdmin && !hasAvonUser && !!userRole;
  const isAdminWithAvon = isAdmin && hasAvonUser;
  const isRegWithAvon = !!userRole && hasAvonUser;
  const isCreatingAvon = isAdminNoAvon || isRegNoAvon;

  return (
    <div style={{ flex: 1 }}>
      <Typography variant="h6">Avon Configuration</Typography>
      <div style={{ flexDirection: 'row' }}>
        {!userRole && !isAdmin && !hasAvonUser && (
          <FieldWrapper>Select user's role first</FieldWrapper>
        )}
        {isCreatingAvon && (
          <>
            <Typography variant="titleSmall" sx={{ padding: '8px 0px' }}>
              Select User's Role
            </Typography>
            <FieldWrapper>{specializationPicker}</FieldWrapper>
          </>
        )}
        {(isRegWithAvon || isAdminWithAvon || isCreatingAvon) && (
          <>
            <Typography variant="titleSmall" sx={{ padding: '8px 0px' }}>
              Select Medical Centers
            </Typography>
            <FieldWrapper>
              <TableContainer component={Card}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="avon medical center management table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Medical Center</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {avonUserDetails?.medical_centers?.map((row, index) => {
                      const medCenterConfig = medicalCenters?.find(
                        client => client.id === row
                      );
                      return (
                        <TableRow
                          key={`${row}_row${index}`}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {medCenterConfig?.name}
                          </TableCell>
                          <TableCell align="center">
                            <a
                              onClick={() => removeMedCenter(row)}
                              style={{ color: 'red' }}
                            >
                              Remove
                            </a>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {medCenterPicker}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          debounced
                          disabled={!selectedMedCenterId}
                          onClick={addMedCenter}
                        >
                          Add Center
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </FieldWrapper>
          </>
        )}
      </div>
    </div>
  );
};

export default EditAvon;
